import { Lock01 } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';

import { NativeSelect } from '@/lib/base/forms/select/select-native';

import type { UpdateMappingBody } from '../api/fieldMappings';
import type { AhoyPropertyOption, HubspotFieldMapping } from '../types';

export type MappingUpdate = UpdateMappingBody;

interface EditableMappingRowProps {
  mapping: HubspotFieldMapping;
  availableAhoyProperties: AhoyPropertyOption[];
  isSaving: boolean;
  onUpdate: (mappingId: number, body: MappingUpdate) => void;
}

const NOT_MAPPED_VALUE = '';

// Turning a field on/off is driven by the Ahoy-property control ("Don't sync" vs
// a property), so Direction only offers the three real directions — no "skipped".
const DIRECTION_OPTIONS = [
  { value: 'bidirectional', label: 'Both directions' },
  { value: 'ahoy_to_external', label: 'Ahoy → HubSpot' },
  { value: 'external_to_ahoy', label: 'HubSpot → Ahoy' },
];
const DIRECTION_VALUES = new Set(DIRECTION_OPTIONS.map(o => o.value));

const CONFLICT_RESOLUTION_OPTIONS = [
  { value: 'most_recent', label: 'Most recent wins' },
  { value: 'ahoy_wins', label: 'Ahoy wins' },
  { value: 'external_wins', label: 'HubSpot wins' },
];

// Shared grid template so the column-header row lines up with each row's controls.
// Four compact columns: field, Ahoy property, direction, on-conflict.
export const MAPPING_ROW_GRID =
  'sm:grid sm:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] sm:items-center sm:gap-3';

const MOBILE_LABEL_CLASS =
  'mb-1 block text-xs font-medium text-[var(--color-text-tertiary)] sm:hidden';

// Compact select. `min-w-0` lets the <select> shrink below its longest option's
// intrinsic width (it sits in NativeSelect's inner `grid` wrapper, whose item
// defaults to min-width:auto and would otherwise overflow the column); `pr-10`
// keeps the value text clear of the absolutely-positioned chevron.
const SELECT_CLASS = 'min-w-0 py-1.5 text-sm pr-10';

export function EditableMappingRow({
  mapping,
  availableAhoyProperties,
  isSaving,
  onUpdate,
}: EditableMappingRowProps) {
  const isLocked = mapping.is_required_in_hubspot;
  const isMapped = !!mapping.ahoy_property;
  const direction = DIRECTION_VALUES.has(mapping.sync_direction)
    ? mapping.sync_direction
    : 'bidirectional';
  const showConflict = isMapped && direction === 'bidirectional';

  const propertyOptions = useMemo(
    () => [
      { value: NOT_MAPPED_VALUE, label: "Don't sync" },
      ...availableAhoyProperties.map(p => ({ value: p.name, label: p.label })),
    ],
    [availableAhoyProperties]
  );

  const handlePropertyChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { value } = e.target;
      if (!value) {
        onUpdate(mapping.id, {
          ahoy_property: null,
          sync_direction: 'skipped',
        });
        return;
      }
      if (!isMapped) {
        onUpdate(mapping.id, {
          ahoy_property: value,
          sync_direction: 'bidirectional',
          conflict_resolution: 'most_recent',
        });
        return;
      }
      onUpdate(mapping.id, { ahoy_property: value });
    },
    [mapping.id, isMapped, onUpdate]
  );

  const handleDirectionChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { value } = e.target;
      if (value === 'bidirectional' && !mapping.conflict_resolution) {
        onUpdate(mapping.id, {
          sync_direction: value,
          conflict_resolution: 'most_recent',
        });
        return;
      }
      onUpdate(mapping.id, { sync_direction: value });
    },
    [mapping.id, mapping.conflict_resolution, onUpdate]
  );

  const handleConflictChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onUpdate(mapping.id, { conflict_resolution: e.target.value });
    },
    [mapping.id, onUpdate]
  );

  const dotColor = isMapped
    ? 'bg-[var(--color-fg-success-primary)]'
    : 'bg-[var(--color-fg-quaternary)]';

  return (
    <div className={`px-4 py-2.5 ${MAPPING_ROW_GRID}`}>
      {/* HubSpot field identity */}
      <div className='flex min-w-0 items-center gap-2.5'>
        <span
          className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotColor}`}
          aria-hidden='true'
        />
        <span className='truncate text-sm font-medium text-[var(--color-text-primary)]'>
          {mapping.external_property}
        </span>
        <span className='truncate text-xs text-[var(--color-text-tertiary)]'>
          {mapping.hubspot_field_type}
          {mapping.is_required_in_hubspot && (
            <span className='text-[var(--color-text-warning-primary)]'>
              {' · required'}
            </span>
          )}
        </span>
        {isLocked && (
          <Lock01
            className='h-3.5 w-3.5 shrink-0 text-[var(--color-text-tertiary)]'
            aria-label='Locked'
          />
        )}
      </div>

      {/* Ahoy property (also the on/off control via "Don't sync") */}
      <div className='mt-2 min-w-0 sm:mt-0'>
        <span className={MOBILE_LABEL_CLASS}>Ahoy property</span>
        <NativeSelect
          aria-label={`Ahoy property for ${mapping.external_property}`}
          options={propertyOptions}
          value={mapping.ahoy_property ?? NOT_MAPPED_VALUE}
          onChange={handlePropertyChange}
          disabled={isLocked || isSaving}
          selectClassName={SELECT_CLASS}
        />
      </div>

      {/* Direction */}
      <div className='mt-2 min-w-0 sm:mt-0'>
        <span className={MOBILE_LABEL_CLASS}>Direction</span>
        {isMapped ? (
          <NativeSelect
            options={DIRECTION_OPTIONS}
            value={direction}
            onChange={handleDirectionChange}
            disabled={isLocked || isSaving}
            selectClassName={SELECT_CLASS}
          />
        ) : (
          <p className='text-sm text-[var(--color-text-tertiary)]'>
            Not syncing
          </p>
        )}
      </div>

      {/* On conflict — only meaningful for two-way sync */}
      <div className='mt-2 min-w-0 sm:mt-0'>
        <span className={MOBILE_LABEL_CLASS}>On conflict</span>
        {showConflict ? (
          <NativeSelect
            options={CONFLICT_RESOLUTION_OPTIONS}
            value={mapping.conflict_resolution ?? 'most_recent'}
            onChange={handleConflictChange}
            disabled={isLocked || isSaving}
            selectClassName={SELECT_CLASS}
          />
        ) : (
          <span
            className='hidden text-sm text-[var(--color-text-tertiary)] sm:inline'
            title='Conflict resolution only applies to two-way sync'
          >
            —
          </span>
        )}
      </div>
    </div>
  );
}
